import { createFileRoute, redirect, Link, useNavigate } from '@tanstack/react-router';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from '@/components/ui/table';
import {
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  Ban,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import {
  useJournalEntry,
  usePostJournalEntry,
  useCancelJournalEntry,
} from '@/hooks/queries/useJournalEntries';
import { JOURNAL_TYPE_LABELS } from '@/types/accounting';
import { toast } from 'sonner';

export const Route = createFileRoute('/apps/accounting/entries/$entryId')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
  },
  component: EntryDetailPage,
});

function EntryDetailPage() {
  const { entryId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const { data: entry, isLoading } = useJournalEntry(entryId);
  const postEntry = usePostJournalEntry();
  const cancelEntry = useCancelJournalEntry();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handlePost = async () => {
    if (!user?.id) return;
    if (!window.confirm('Êtes-vous sûr de vouloir comptabiliser cette écriture ?')) return;

    try {
      await postEntry.mutateAsync({ entryId, userId: user.id });
      toast.success('✅ Écriture comptabilisée avec succès');
    } catch (error: any) {
      toast.error(`Erreur: ${error.message}`);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette écriture ?')) return;

    try {
      await cancelEntry.mutateAsync(entryId);
      toast.success('Écriture annulée avec succès');
    } catch (error: any) {
      toast.error(`Erreur: ${error.message}`);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  if (!entry) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-lg font-medium">Écriture non trouvée</p>
          <Link to="/apps/accounting/entries">
            <Button className="mt-4">Retour à la liste</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const journalLabel = JOURNAL_TYPE_LABELS[entry.journal_type];
  const statusInfo = {
    draft: { label: 'Brouillon', labelAr: 'مسودة', color: 'yellow' },
    posted: { label: 'Comptabilisé', labelAr: 'محاسب', color: 'green' },
    cancelled: { label: 'Annulé', labelAr: 'ملغى', color: 'red' },
  }[entry.status];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate({ to: '/apps/accounting/entries' })}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-3xl font-bold font-mono">{entry.entry_number}</h1>
              <p className="text-muted-foreground mt-1">
                Écriture comptable / قيد محاسبي
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {entry.status === 'draft' && (
              <Button
                onClick={handlePost}
                variant="default"
                className="bg-gradient-to-r from-emerald-500 to-teal-600"
                disabled={!entry.is_balanced || postEntry.isPending}
              >
                {postEntry.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Comptabiliser
              </Button>
            )}
            {entry.status === 'posted' && (
              <Button onClick={handleCancel} variant="destructive" disabled={cancelEntry.isPending}>
                {cancelEntry.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Ban className="w-4 h-4 mr-2" />
                )}
                Annuler l'écriture
              </Button>
            )}
          </div>
        </div>

        {/* Entry Header Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Informations générales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Date d'écriture</p>
                  <p className="font-medium">{formatDate(entry.entry_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Type de journal</p>
                  <Badge variant="outline" className="mt-1">
                    {journalLabel.fr} / {journalLabel.ar}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Statut</p>
                  <Badge
                    variant="outline"
                    className={`mt-1 bg-${statusInfo.color}-100 text-${statusInfo.color}-700 border-${statusInfo.color}-300`}
                  >
                    {statusInfo.label} / {statusInfo.labelAr}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Équilibre</p>
                  {entry.is_balanced ? (
                    <div className="flex items-center gap-1 text-green-600 mt-1">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-sm font-medium">Équilibré</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-red-600 mt-1">
                      <AlertCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Non équilibré</span>
                    </div>
                  )}
                </div>
              </div>

              {entry.description && (
                <div>
                  <p className="text-xs text-muted-foreground">Description</p>
                  <p className="text-sm mt-1">{entry.description}</p>
                </div>
              )}

              {entry.reference_type && entry.reference_id && (
                <div>
                  <p className="text-xs text-muted-foreground">Référence</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary">{entry.reference_type}</Badge>
                    <code className="text-xs bg-muted px-2 py-1 rounded">
                      {entry.reference_id.substring(0, 8)}...
                    </code>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Totaux / المجاميع</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-xs text-blue-700">Total Débit / إجمالي المدين</p>
                  <p className="text-2xl font-bold text-blue-900 mt-1">
                    {formatCurrency(entry.total_debit)}
                  </p>
                  <p className="text-xs text-blue-700 mt-1">DA</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-xs text-green-700">Total Crédit / إجمالي الدائن</p>
                  <p className="text-2xl font-bold text-green-900 mt-1">
                    {formatCurrency(entry.total_credit)}
                  </p>
                  <p className="text-xs text-green-700 mt-1">DA</p>
                </div>
              </div>

              {entry.posted_at && (
                <div className="pt-3 border-t">
                  <p className="text-xs text-muted-foreground">Comptabilisé le</p>
                  <p className="text-sm font-medium mt-1">{formatDateTime(entry.posted_at)}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Entry Lines */}
        <Card>
          <CardHeader>
            <CardTitle>Lignes d'écriture / سطور القيد ({entry.lines?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-bold">N°</TableHead>
                    <TableHead className="font-bold">Compte</TableHead>
                    <TableHead className="font-bold">Libellé</TableHead>
                    <TableHead className="font-bold text-right">Débit</TableHead>
                    <TableHead className="font-bold text-right">Crédit</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entry.lines?.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell className="font-mono text-muted-foreground">
                        {line.line_number}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-mono font-bold">{line.account_code}</span>
                          {line.description && (
                            <span className="text-xs text-muted-foreground">
                              {line.description}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        {line.description || '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-blue-600 font-bold">
                        {line.debit > 0 ? formatCurrency(line.debit) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600 font-bold">
                        {line.credit > 0 ? formatCurrency(line.credit) : '-'}
                      </TableCell>
                      <TableCell>
                        <Link
                          to="/apps/accounting/reports/general-ledger"
                          search={{ accountId: line.account_id }}
                        >
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="w-3 h-3" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow className="bg-muted font-bold">
                    <TableCell colSpan={3}>TOTAL / المجموع</TableCell>
                    <TableCell className="text-right font-mono text-blue-700">
                      {formatCurrency(entry.total_debit)} DA
                    </TableCell>
                    <TableCell className="text-right font-mono text-green-700">
                      {formatCurrency(entry.total_credit)} DA
                    </TableCell>
                    <TableCell>
                      {entry.is_balanced ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3 text-sm text-blue-800">
              <div>
                <p className="font-medium">Écriture en partie double (SCF)</p>
                <ul className="mt-2 space-y-1 text-xs">
                  <li>✓ Chaque écriture doit être équilibrée (Débit = Crédit)</li>
                  <li>✓ Les écritures comptabilisées mettent à jour les soldes des comptes</li>
                  <li>✓ Les écritures annulées n'affectent pas les soldes</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
